import React, { useRef, useState } from "react";
import {
  AnchorLink,
  Button,
  Container,
  Input,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";
import {
  useAppDispatch,
  useAppSelector,
  useHandlePageNavigate,
} from "../../../hooks/hooks";
import ProtectionShell from "./ProtectionShell";
import {
  selectProtectionRequestStatus,
  selectProtectionState,
  sendProtectionVerificationCodeAsync,
  verifyProtectionVerificationCodeAsync,
} from "../../../features/profileProtection/profileProtectionSlice";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";

const Verification: React.FC = () => {
  const dispatch = useAppDispatch();
  const handleNavigate = useHandlePageNavigate();
  const { verificationEmail } = useAppSelector(selectProtectionState);
  const requestStatus = useAppSelector(selectProtectionRequestStatus);
  const [otp, setOtp] = useState<string>("");
  const [codeSent, setCodeSent] = useState<boolean>(false);
  const [invalidCode, setInvalidCode] = useState<boolean>(false);
  const hasSentVerificationViewedEvent = useRef(false);
  const hasSentEmailVerifiedEvent = useRef(false);

  React.useEffect(() => {
    if (!hasSentVerificationViewedEvent.current) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Profile Protection",
          EventAction: "Verification Viewed",
        }),
      );
      hasSentVerificationViewedEvent.current = true;
    }
  }, [dispatch]);

  const handleVerify = async () => {
    if (!otp.trim()) {
      return;
    }

    try {
      await dispatch(
        verifyProtectionVerificationCodeAsync({
          code: otp,
        }),
      ).unwrap();
    } catch {
      setInvalidCode(true);
      return;
    }

    if (!hasSentEmailVerifiedEvent.current) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Profile Protection",
          EventAction: "Email Verified",
        }),
      );
      hasSentEmailVerifiedEvent.current = true;
    }

    handleNavigate("/protection/remove");
  };

  const handleResend = async () => {
    if (!verificationEmail || requestStatus === "loading") {
      return;
    }

    try {
      await dispatch(
        sendProtectionVerificationCodeAsync({
          email: verificationEmail,
        }),
      ).unwrap();
      setCodeSent(true);
    } catch {
      setCodeSent(false);
    }
  };

  return (
    <ProtectionShell
      title="Verify your email"
      subtitle="Enter the 7-digit code we sent to confirm access to your email."
      stepLabel="Step 2 of 3"
    >
      <Padding top="sm">
        <Container border bodyBorderRadius borderColor="primary">
          <Padding space="lg">
            <Layout
              type="flex"
              flexProps={{
                direction: "column",
                gap: "sm",
                alignItems: "center",
              }}
            >
              <Layout
                type="flex"
                flexProps={{
                  direction: "column",
                  gap: "xs",
                  alignItems: "center",
                }}
              >
                <Text textAlign="center" fontWeight="semibold">
                  Enter verification code
                </Text>
                <Text size="xs" textAlign="center" color="dark-gray">
                  We sent a 7-digit code to{" "}
                </Text>
                <Text size="xs" fontWeight="semibold">
                  {verificationEmail || "your email"}
                </Text>
              </Layout>

              <Input
                placeholder="Enter 7-digit code"
                value={otp}
                handleChange={(val: string) => {
                  setOtp(val.replace(/\D/g, "").slice(0, 7));
                  setInvalidCode(false);
                }}
                id="protection-otp"
                type="text"
              />
              {invalidCode && (
                <Text size="xs" color="error">
                  Invalid code. Please try again.
                </Text>
              )}
              <Button
                buttonText={
                  requestStatus === "loading"
                    ? "Verifying..."
                    : "Verify & Continue"
                }
                color="primary"
                handleClick={handleVerify}
                disabled={otp.length !== 7 || requestStatus === "loading"}
                loading={requestStatus === "loading"}
                fill="fill"
              />
              <AnchorLink
                role="button"
                handleClick={handleResend}
                hoverUnderline
              >
                {requestStatus === "loading"
                  ? "Sending..."
                  : codeSent
                    ? "Resend Code"
                    : "Send Code"}
              </AnchorLink>
            </Layout>
          </Padding>
        </Container>
      </Padding>

      <Padding top="md">
        <Layout type="flex" flexProps={{ justifyContent: "center" }}>
          <AnchorLink
            role="button"
            handleClick={() => handleNavigate("/protection/info")}
          >
            Change Email
          </AnchorLink>
        </Layout>
      </Padding>
    </ProtectionShell>
  );
};

export default Verification;
